import { useState } from "react";

import {
  labeebTenantQuotaPath,
  tierDisplayName,
  tierRefsOf,
  type LabeebCatalog,
  type LabeebTenantQuota,
} from "../../api/labeeb";
import type { AppConfig } from "../../config";
import { Button, Select } from "../../f7";
import { useResource } from "../../hooks/useResource";
import { useWrite } from "../../hooks/useWrite";
import { Gate } from "../authority/Gate";
import { ResourceView } from "../states";
import { Badge, Field, Fields } from "../ui";
import { ConfirmDialog } from "../write/ConfirmDialog";
import { WriteResult } from "../write/EffectTiming";
import { WriteFailure } from "../write/WriteFailure";

/**
 * This merchant's Labeeb tier, and the allowance it carries.
 *
 * REPLACES the `tier_id` half of `LabeebTenantControl`. The old
 * `PATCH /admin/tenant/{id}` took tier and agents together on one route; the
 * new API splits them, and the tier lives in its own versioned document at
 * `/admin/tenant/{id}/quota` alongside the daily and monthly numbers it
 * resolves to.
 *
 * `source` is the field worth rendering and the one the old UI had no way to
 * express: it says whether the numbers shown are this merchant's OWN or
 * inherited from the tier. An operator editing an inherited value is creating
 * an override where none existed, which is a different act from adjusting one
 * that already exists.
 */
export function LabeebQuotaControl({
  config,
  tenantId,
  catalog,
}: {
  config: AppConfig;
  tenantId: string;
  catalog: ReturnType<typeof useResource<LabeebCatalog>>;
}) {
  const quota = useResource<LabeebTenantQuota>(config, labeebTenantQuotaPath(tenantId), [tenantId]);
  const write = useWrite<LabeebTenantQuota>(config);
  const [choice, setChoice] = useState("");
  const [confirming, setConfirming] = useState(false);
  // This control stays mounted when the merchant changes. The tier picked
  // for the one who was on screen, and a confirm already open, belong to
  // them — saving either would write their tier onto the next business.
  // Cleared here, before the next quota arrives, the same way the domain
  // editor drops its edit.
  const [chosenFor, setChosenFor] = useState(tenantId);
  if (chosenFor !== tenantId) {
    setChosenFor(tenantId);
    setChoice("");
    setConfirming(false);
  }

  const s = quota.state;
  const busy = write.state.kind === "submitting";
  const cat = catalog.state.kind === "ready" ? catalog.state.data : null;
  const current = s.kind === "ready" ? s.data : null;
  const version = current?.version;
  const tiers = tierRefsOf(cat);

  function submit(reason: string) {
    if (version === undefined || !choice) return;
    void write
      .submit(labeebTenantQuotaPath(tenantId), {
        method: "PATCH",
        body: JSON.stringify({ expected_version: version, reason, tier_id: choice }),
      })
      .then((r) => {
        if (!r) return;
        quota.reload();
        setChoice("");
      });
    setConfirming(false);
  }

  return (
    <>
      <ResourceView
        state={s}
        what="this merchant's tier"
        onRetry={quota.reload}
        notFound={
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Labeeb holds no quota document for this merchant.
          </p>
        }
      />

      {s.kind === "ready" && (
        <>
          <Fields>
            <Field label="Tier">
              <span className="ref">{tierDisplayName(cat, current?.tier_id ?? "")}</span>
            </Field>
            <Field label="Daily">
              <span className="tabular-nums">{limitText(current?.daily)}</span>
            </Field>
            <Field label="Monthly">
              <span className="tabular-nums">{limitText(current?.monthly)}</span>
            </Field>
            <Field label="From">
              {/* `tenant` means an override exists for this merchant;
                  `tier` means these are the tier's own numbers. */}
              {current?.source === "tenant" ? (
                <Badge tone="amber">this merchant</Badge>
              ) : (
                <span className="text-xs text-slate-500 dark:text-slate-400">
                  the tier{current?.default_tier ? ` (default: ${current.default_tier})` : ""}
                </span>
              )}
            </Field>
          </Fields>

          <Gate need="quota:write">
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <div className="w-56 max-w-full">
                <Select
                  value={choice}
                  aria-label="Tier to assign"
                  onChange={(e) => setChoice(e.target.value)}
                >
                  <option value="">Change tier…</option>
                  {tiers
                    .filter((t) => t.tier_id !== current?.tier_id)
                    .map((t) => (
                      <option key={t.tier_id} value={t.tier_id}>
                        {t.display_name ?? t.tier_id}
                      </option>
                    ))}
                </Select>
              </div>
              <Button
                disabled={busy || !choice || version === undefined}
                onClick={() => setConfirming(true)}
              >
                Assign
              </Button>
            </div>
          </Gate>
        </>
      )}

      {write.state.kind === "done" && (
        <div className="mt-3">
          <WriteResult title="Tier assigned" onDismiss={write.reset}>
            <p className="text-sm">
              This merchant is on <span className="ref">{tierDisplayName(cat, choice)}</span>.
              It applies from their next turn.
            </p>
          </WriteResult>
        </div>
      )}
      <div className="mt-3">
        <WriteFailure
          state={write.state}
          onDismiss={write.reset}
          onReread={() => {
            write.reset();
            quota.reload();
          }}
        />
      </div>

      <ConfirmDialog
        open={confirming}
        title="Change this merchant's Labeeb tier"
        confirmLabel="Assign tier"
        requireReason
        reasonHint="e.g. DA-1234, upgraded after sales call"
        busy={busy}
        onConfirm={submit}
        onCancel={() => setConfirming(false)}
        effect="Applies from this merchant's next turn."
      >
        <p>
          <span className="ref">{tenantId}</span> moves from{" "}
          <span className="ref">{tierDisplayName(cat, current?.tier_id ?? "")}</span> to{" "}
          <span className="ref">{tierDisplayName(cat, choice)}</span>.
        </p>
        <p>
          Their daily and monthly allowance becomes whatever that tier grants, unless an
          override is set for them specifically.
        </p>
      </ConfirmDialog>
    </>
  );
}

/** `-1` means unlimited on this API; a missing number is not a zero. */
function limitText(n: number | undefined): string {
  if (typeof n !== "number") return "—";
  return n < 0 ? "unlimited" : String(n);
}
